import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { MODES, DENSITIES } from '../../constants/themes';
import './AppearanceMenu.css';

/**
 * AppearanceMenu — player-facing picker for colour theme, light/dark mode, and
 * density. A small popover triggered from the TopBar. Selections persist via
 * ThemeContext (localStorage). Implements the user-facing appearance controls
 * from docs/frontend/DESIGN_REDESIGN.md (§3.6, §6.2).
 */
function AppearanceMenu() {
  const { themeId, mode, densityId, themes, setTheme, setMode, setDensity } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        // Return focus to the trigger so keyboard users aren't dropped at <body>.
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Close when keyboard focus leaves the popover entirely (e.g. Tab past the
  // last control), so focus never silently lands behind an open popover.
  const handleBlurCapture = (e) => {
    if (ref.current && !ref.current.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div className="appearance" ref={ref} onBlurCapture={handleBlurCapture}>
      <button
        ref={triggerRef}
        className="appearance-trigger"
        onClick={() => setOpen((o) => !o)}
        title="Appearance"
        aria-label="Appearance settings"
        aria-expanded={open}
      >
        🎨
      </button>

      {open && (
        <div className="appearance-popover" role="group" aria-label="Appearance settings">
          <div className="appearance-section">
            <span className="appearance-heading">Theme</span>
            <div className="appearance-themes">
              {themes.map((t) => (
                <button
                  key={t.id}
                  aria-pressed={t.id === themeId}
                  className={`appearance-theme ${t.id === themeId ? 'is-selected' : ''}`}
                  onClick={() => setTheme(t.id)}
                  title={t.blurb}
                >
                  <span className="appearance-swatch" style={{ background: t.swatch }} />
                  <span className="appearance-theme-name">{t.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="appearance-section">
            <span className="appearance-heading">Mode</span>
            <div className="appearance-segment">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  aria-pressed={m.id === mode}
                  className={`appearance-seg-btn ${m.id === mode ? 'is-selected' : ''}`}
                  onClick={() => setMode(m.id)}
                >
                  <span>{m.icon}</span> {m.name}
                </button>
              ))}
            </div>
          </div>

          <div className="appearance-section">
            <span className="appearance-heading">Density</span>
            <div className="appearance-segment">
              {DENSITIES.map((d) => (
                <button
                  key={d.id}
                  aria-pressed={d.id === densityId}
                  className={`appearance-seg-btn ${d.id === densityId ? 'is-selected' : ''}`}
                  onClick={() => setDensity(d.id)}
                  title={d.blurb}
                >
                  {d.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppearanceMenu;
